// ABOUTME: Admin-only live preview of the MUI theme — palette, typography, Avatar
// ABOUTME: contrastText states, and a sample of theme/tokens.ts, for visual spot-checking
import React, { useState } from 'react';
import { Container, Box, Typography, Grid, Paper, Avatar, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useTheme, ThemeProvider } from '@mui/material/styles';
import { celebrationGold, statusTint, ladderPositionColors, streakIntensityColors, behaviorHeartDark } from '../theme/tokens';
import { themeRegistry, resolveTheme, type ThemeName } from '../theme/themeRegistry';

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// Matches the labels used in the real theme pickers (AdminSettings.tsx, both
// EditFamilyInfoModal.tsx files) rather than deriving one from the registry key.
const THEME_DISPLAY_NAMES: Record<string, string> = {
  'nalu-v1': 'Nalu',
};

function themeDisplayName(name: string) {
  return THEME_DISPLAY_NAMES[name] ?? capitalize(name);
}

function ColorSwatch({ label, bg, value, textColor }: { label: string; bg: string; value: string; textColor?: string }) {
  return (
    <Box sx={{ width: 140, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ height: 64, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {textColor && <Typography sx={{ color: textColor, fontSize: '0.75rem' }}>Aa</Typography>}
      </Box>
      <Box sx={{ p: 1 }}>
        <Typography variant="caption" display="block" fontWeight={600}>{label}</Typography>
        <Typography variant="caption" display="block" color="text.secondary">{value}</Typography>
      </Box>
    </Box>
  );
}

const STANDARD_PALETTE_GROUPS = ['primary', 'secondary', 'success', 'warning', 'error', 'info'] as const;

function PaletteSection() {
  const theme = useTheme();
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>Palette</Typography>
      {STANDARD_PALETTE_GROUPS.map((key) => {
        const group = theme.palette[key];
        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography variant="h6">{capitalize(key)}</Typography>
            <Grid container spacing={2}>
              <Grid><ColorSwatch label="main" bg={group.main} value={group.main} /></Grid>
              <Grid><ColorSwatch label="light" bg={group.light} value={group.light} /></Grid>
              <Grid><ColorSwatch label="dark" bg={group.dark} value={group.dark} /></Grid>
              <Grid><ColorSwatch label="contrastText" bg={group.main} value={group.contrastText} textColor={group.contrastText} /></Grid>
            </Grid>
          </Box>
        );
      })}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Background</Typography>
        <Grid container spacing={2}>
          <Grid><ColorSwatch label="default" bg={theme.palette.background.default} value={theme.palette.background.default} /></Grid>
          <Grid><ColorSwatch label="paper" bg={theme.palette.background.paper} value={theme.palette.background.paper} /></Grid>
        </Grid>
      </Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Gradient</Typography>
        <Grid container spacing={2}>
          <Grid><ColorSwatch label="primary" bg={theme.palette.gradient.primary} value={theme.palette.gradient.primary} /></Grid>
          <Grid><ColorSwatch label="secondary" bg={theme.palette.gradient.secondary} value={theme.palette.gradient.secondary} /></Grid>
        </Grid>
      </Box>
      <Box>
        <Typography variant="h6">Behavior Heart</Typography>
        <Grid container spacing={2}>
          <Grid><ColorSwatch label="behaviorHeart" bg={theme.palette.behaviorHeart} value={theme.palette.behaviorHeart} /></Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

const TYPOGRAPHY_VARIANTS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'body1', 'body2', 'button'] as const;

function TypographySection() {
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>Typography</Typography>
      {TYPOGRAPHY_VARIANTS.map((variant) => (
        <Typography key={variant} variant={variant} sx={{ mb: 1 }}>
          {variant} — The quick brown fox jumps over the lazy dog
        </Typography>
      ))}
    </Paper>
  );
}

const AVATAR_STATES = ['primary', 'success', 'warning', 'error', 'info'] as const;

function AvatarSection() {
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>Avatar contrastText states</Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {AVATAR_STATES.map((key) => (
          <Box key={key} sx={{ textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: `${key}.main`, color: `${key}.contrastText` }}>A</Avatar>
            <Typography variant="caption" display="block">{capitalize(key)}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function TokensSection() {
  const theme = useTheme();
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Tokens (representative sample)</Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Celebration</Typography>
        <Grid container spacing={2}>
          <Grid><ColorSwatch label="celebrationGold" bg={celebrationGold} value={celebrationGold} /></Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Status tints</Typography>
        <Grid container spacing={2}>
          {Object.entries(statusTint).map(([name, value]) => (
            <Grid key={name}><ColorSwatch label={name} bg={value} value={value} /></Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Ladder position</Typography>
        <Grid container spacing={2}>
          {ladderPositionColors.map((value, i) => (
            <Grid key={i}><ColorSwatch label={`position ${i + 1}`} bg={value} value={value} /></Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Streak intensity</Typography>
        <Grid container spacing={2}>
          {Object.entries(streakIntensityColors).map(([name, value]) => (
            <Grid key={name}><ColorSwatch label={name} bg={value} value={value} /></Grid>
          ))}
        </Grid>
      </Box>

      <Box>
        <Typography variant="h6">Behavior accents</Typography>
        <Grid container spacing={2}>
          <Grid><ColorSwatch label="oneOnOneAccent" bg={theme.palette.oneOnOneAccent} value={theme.palette.oneOnOneAccent} /></Grid>
          <Grid><ColorSwatch label="behaviorHeartDark" bg={behaviorHeartDark} value={behaviorHeartDark} /></Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export default function AdminThemePreview() {
  const [selectedThemeName, setSelectedThemeName] = useState<ThemeName>('default');

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Theme Preview
        </Typography>

        <FormControl sx={{ mb: 3, minWidth: 200 }}>
          <InputLabel id="theme-preview-selector-label">Preview theme</InputLabel>
          <Select
            labelId="theme-preview-selector-label"
            value={selectedThemeName}
            onChange={(e) => setSelectedThemeName(e.target.value as ThemeName)}
            label="Preview theme"
          >
            {Object.keys(themeRegistry).map((name) => (
              <MenuItem key={name} value={name}>{themeDisplayName(name)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This only changes what&apos;s shown below — it doesn&apos;t affect your family&apos;s or the site&apos;s actual theme setting.
        </Typography>

        <ThemeProvider theme={resolveTheme(selectedThemeName)}>
          <PaletteSection />
          <TypographySection />
          <AvatarSection />
          <TokensSection />
        </ThemeProvider>
      </Box>
    </Container>
  );
}
